import React from 'react';
import { Button, Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PageWrapper from 'components/Common/PageWraper';
import PageHeader from 'components/Header/Page';
import MetricCardWidget from 'components/Widgets/MetricCardWidget';
import { useKpiTrend } from 'hooks/useKpiTrend';
import KpiCardNew from 'components/Widgets/KpiCardNew';

export default function MetricPage() {
  const { getKpiStatus, getBgColorByStatus } = useKpiTrend();
  const navigate = useNavigate();

  const performanceStatusTooltip = [
    {
      title: 'Green background',
      description: 'Performance improved from the previous month.',
    },
    {
      title: 'Amber background',
      description:
        'Performance shows no significant change from the previous month but is still outside the defined target and can be improved.',
    },
    {
      title: 'Red background',
      description: 'Performance has worsened compared to the previous month.',
    },
  ];

  const metricsApiResponse = [
    {
      key: 'incidentVolume',
      title: 'Incident Volume',
      current: 1880,
      previous: 1790,
      reverseTrend: false,
    },
    {
      key: 'mttr',
      title: 'MTTR',
      current: 153,
      previous: 153,
      target: 120,
      reverseTrend: true,
      unit: 'Hrs',
    },
    {
      key: 'responseSla',
      title: 'Response SLA',
      current: 97.1,
      previous: 90.5,
      target: 95,
      reverseTrend: false,
      unit: '%',
    },
    {
      key: 'cloudSpend',
      title: 'Cloud Spend',
      current: 130000,
      previous: 125000,
      target: 110000,
      reverseTrend: true,
      isCost: true,
    },
  ];

  const demoKpiData = {
    totalUsers: {
      value: 2450,
    },
    activeUsers: {
      value: 1875,
      change: 8.4,
      changeType: 'increase' as const,
    },
    topUsers: {
      value: 125,
      change: 12.5,
      changeType: 'increase' as const,
    },
    adoptionRate: {
      value: 76.5,
      change: 5.2,
      changeType: 'increase' as const,
    },
    averageLoginsPerWeek: {
      value: 4.8,
      change: 3.6,
      changeType: 'increase' as const,
    },
    newUsersThisWeek: {
      value: 84,
      change: 6.3,
      changeType: 'increase' as const,
    },
    averageTimeInPlatform: {
      value: '24m 36s',
      change: 4.7,
      changeType: 'increase' as const,
    },
  };

  const mappedMetrics = metricsApiResponse.map((metric) => {
    const status = getKpiStatus({
      current: metric.current,
      previous: metric.previous,
      target: metric.target,
      reverseTrend: metric.reverseTrend,
    });

    return {
      ...metric,
      bgColor: getBgColorByStatus(status),
      change: Number((((metric.current - metric.previous) / metric.previous) * 100).toFixed(1)),
      changeType: metric.current > metric.previous ? 'increase' : 'decrease',
    };
  });

  return (
    <PageWrapper>
      <PageHeader title="Metric Count Card Demo">
        <Button variant="pepwiseSecondary" onClick={() => navigate(-1)}>
          ← Back
        </Button>
      </PageHeader>

      <Grid container spacing={3} mt={2} mb={2}>
        {mappedMetrics.map((metric) => (
          <Grid item xs={12} md={3} key={metric.key}>
            <MetricCardWidget
              title={metric.title}
              number={metric.current}
              units={metric.unit}
              isCost={metric.isCost}
              reverseTrendColor={metric.reverseTrend}
              change={Math.abs(metric.change)}
              changeType={metric.changeType}
              showChangePercentage
              bgColor={metric.bgColor}
              isLoading={false}
              hasTooltip
              tooltipLabel={performanceStatusTooltip}
            />
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCardNew
            isLoading={false}
            title="Total Users"
            hasTooltip
            tooltipLabel="Total number of users onboarded and having access to PepWise."
            number={demoKpiData.totalUsers.value}
            isCost={false}
            showChangePercentage={false}
            showPeriodChange={false}
            reverseTrendColor={false}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <KpiCardNew
            isLoading={false}
            title="Active Users"
            hasTooltip
            tooltipLabel="Users active in the selected time frame."
            number={demoKpiData.activeUsers.value}
            isCost={false}
            showChangePercentage
            change={demoKpiData.activeUsers.change}
            changeType={demoKpiData.activeUsers.changeType}
            showPeriodChange={false}
            reverseTrendColor={false}
            hasModal
            modalText="View Users"
            onModalClick={() => {
              console.log('Active users clicked');
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <KpiCardNew
            isLoading={false}
            title="Top Users"
            hasTooltip
            tooltipLabel="Number of highest-frequency users based on activity in the selected period compared with the previous 30 days."
            number={demoKpiData.topUsers.value}
            isCost={false}
            showChangePercentage
            change={demoKpiData.topUsers.change}
            changeType={demoKpiData.topUsers.changeType}
            showPeriodChange={false}
            reverseTrendColor={false}
            hasModal
            modalText="View Users"
            onModalClick={() => {
              console.log('Top users clicked');
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <KpiCardNew
            isLoading={false}
            title="Adoption Rate"
            hasTooltip
            tooltipLabel="Percentage of active users relative to total eligible users for the selected period."
            number={demoKpiData.adoptionRate.value}
            isCost={false}
            isPercentage
            showChangePercentage
            change={demoKpiData.adoptionRate.change}
            changeType={demoKpiData.adoptionRate.changeType}
            showPeriodChange={false}
            reverseTrendColor={false}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <KpiCardNew
            isLoading={false}
            title="Average Logins per Week"
            hasTooltip
            tooltipLabel="Average number of times users logged in per week during the selected period."
            number={demoKpiData.averageLoginsPerWeek.value}
            isCost={false}
            showChangePercentage
            change={demoKpiData.averageLoginsPerWeek.change}
            changeType={demoKpiData.averageLoginsPerWeek.changeType}
            showPeriodChange={false}
            reverseTrendColor={false}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <KpiCardNew
            isLoading={false}
            title="New Users this Week"
            hasTooltip
            tooltipLabel="Number of first-time users who joined PepWise this week during the past seven days."
            number={demoKpiData.newUsersThisWeek.value}
            isPercentage={false}
            isCost={false}
            showChangePercentage
            change={demoKpiData.newUsersThisWeek.change}
            changeType={demoKpiData.newUsersThisWeek.changeType}
            showPeriodChange={false}
            reverseTrendColor={false}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={6}>
          <KpiCardNew
            isLoading={false}
            title="Average Time in Platform"
            hasTooltip
            tooltipLabel="Average session duration per user during the selected period."
            number={demoKpiData.averageTimeInPlatform.value}
            isCost={false}
            showChangePercentage
            change={demoKpiData.averageTimeInPlatform.change}
            changeType={demoKpiData.averageTimeInPlatform.changeType}
            showPeriodChange={false}
            reverseTrendColor={false}
          />
        </Grid>
      </Grid>
    </PageWrapper>
  );
}
